import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete, Image as ImageIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { CMSCustomer } from "../../../../services/cms";
import { resolveImageUrl, withImageFallback } from "../../../../utils/imageFallback";

interface CustomersTabProps {
  customers: CMSCustomer[];
  onEdit: (customer?: CMSCustomer) => void;
  onDelete: (id: string) => void;
}

const CustomersTab: React.FC<CustomersTabProps> = ({
  customers,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("admin.contentManagement.customers")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => onEdit()}
          sx={{
            background: "linear-gradient(135deg, #e91e63 0%, #c2185b 100%)",
            boxShadow: "0 2px 8px rgba(233,30,99,0.2)",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {t("admin.contentManagement.addCustomer")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(auto-fill, minmax(140px, 1fr))",
            sm: "repeat(3, 1fr)",
            md: "repeat(4, 1fr)",
            lg: "repeat(6, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {customers.map((customer) => (
          <Card key={customer.id} sx={{ height: "100%", textAlign: "center" }}>
            <CardContent>
              <Box
                sx={{
                  height: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                {customer.logo_url ? (
                  <img
                    src={resolveImageUrl(customer.logo_url, '/placeholder-product.png')}
                    alt={customer.name}
                    onError={(e) => withImageFallback(e, '/placeholder-product.png')}
                    style={{
                      maxHeight: "100%",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <ImageIcon sx={{ fontSize: 48, color: "rgba(0,0,0,0.2)" }} />
                )}
              </Box>

              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" } }}
              >
                {customer.name}
              </Typography>

              <Chip
                label={
                  customer.is_active
                    ? t("admin.contentManagement.active")
                    : t("admin.contentManagement.inactive")
                }
                color={customer.is_active ? "success" : "default"}
                size="small"
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                <Tooltip title={t("admin.contentManagement.edit")}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(customer)}
                    sx={{ color: "#00d4ff" }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("admin.contentManagement.delete")}>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(customer.id!)}
                    sx={{ color: "#ff3366" }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default CustomersTab;